import React, { useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useToast } from '@raptormaps/toast';
import theme from '@raptormaps/theme';
import { TextInput } from '@raptormaps/text-input';
import { Button } from '@raptormaps/button';
import { Text } from '@raptormaps/text';
import { Stack } from '@raptormaps/layout';
import {
  getSolarFarmsByNameQuery,
  getSolarFarmByUuidQuery,
  updateSolarFarmByUuid,
} from '../../api/services/solar-farm';
import { UpdateSolarFarmPayload } from '../../api/services/solar-farm/solar-farm.schema';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { FarmDetailCard } from '../../components/FarmDetailCard';
import { InspectionsTable } from './InspectionsTable';
import { EditFarmModal } from '../../components/EditFarmModal';
import { Icon } from '@raptormaps/icon';
import './FarmDetails.css';

type SearchResult = {
  id: number;
  uuid: string;
  name: string;
  address?: string | null;
};

export function FarmDetails() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const history = useHistory();
  const location = useLocation();
  const selectedUuid = new URLSearchParams(location.search).get('uuid');
  const [searchValue, setSearchValue] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  const debouncedSearch = useDebouncedValue(searchValue, 300);

  const { data: searchResults, isFetching: searching } = useQuery({
    ...getSolarFarmsByNameQuery(debouncedSearch),
    select: data => data.solar_farms as SearchResult[],
    enabled: debouncedSearch.trim().length >= 2 && !selectedUuid,
  });

  const { data: selectedFarm, isLoading: detailLoading } = useQuery({
    ...getSolarFarmByUuidQuery(selectedUuid ?? ''),
    select: data => data.solar_farm,
    enabled: !!selectedUuid,
  });

  const { mutateAsync: doUpdate } = useMutation(
    (payload: UpdateSolarFarmPayload) => {
      if (!selectedFarm?.uuid || !selectedFarm?.org_id) {
        throw new Error('Missing farm uuid or org_id');
      }
      return updateSolarFarmByUuid(
        selectedFarm.uuid,
        selectedFarm.org_id,
        payload,
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          getSolarFarmByUuidQuery(selectedUuid ?? '').queryKey,
        );
      },
    },
  );

  const handleSave = async (payload: UpdateSolarFarmPayload) => {
    const update = doUpdate(payload);
    toast.promise(update, {
      loading: 'Saving farm changes...',
      success: 'Farm updated successfully!',
      error: 'Failed to update farm. Please try again.',
    });
    await update;
    setEditOpen(false);
  };

  const selectFarm = useCallback(
    (farm: SearchResult) => {
      history.push({ search: `?uuid=${farm.uuid}` });
      setSearchValue('');
    },
    [history],
  );

  const clearFarm = useCallback(() => {
    history.push({ search: '' });
    setSearchValue('');
  }, [history]);

  const showDropdown = searchValue.trim().length >= 2 && !selectedUuid;
  const displayedSearchValue = selectedFarm
    ? selectedFarm.name ?? ''
    : searchValue;

  return (
    <div className="fd-page">
      <div className="farm-search-section">
        <div className="farm-search-wrapper">
          <div className="farm-search-input-wrap">
            <Text as="span" className="farm-search-icon" aria-hidden>
              <Icon icon="MagnifyingGlass" size="small" color="neutral_600" />
            </Text>
            <TextInput
              placeholder="Search by farm name..."
              value={displayedSearchValue}
              onChange={e => {
                if (selectedUuid) clearFarm();
                setSearchValue(e.target.value);
              }}
            />
            {selectedUuid && (
              <Button
                variant="tertiary"
                className="farm-search-clear"
                onClick={clearFarm}
                aria-label="Clear selection"
              >
                ✕
              </Button>
            )}
          </div>

          {showDropdown && (
            <div className="farm-search-dropdown">
              {searching ? (
                <div className="farm-search-empty">
                  <Text variant="paragraph_small" color="neutral_600">
                    Searching...
                  </Text>
                </div>
              ) : !searchResults || searchResults.length === 0 ? (
                <div className="farm-search-empty">
                  <Text variant="paragraph_small" color="neutral_600">
                    No farms found
                  </Text>
                </div>
              ) : (
                searchResults.map(farm => (
                  <Button
                    key={farm.id}
                    variant="tertiary"
                    className="farm-search-option"
                    onClick={() => selectFarm(farm)}
                  >
                    <div>
                      <Text variant="paragraph_small_bold">{farm.name}</Text>
                      {farm.address && (
                        <Text variant="paragraph_xsmall" color="neutral_600">
                          {farm.address}
                        </Text>
                      )}
                    </div>
                    <Text variant="paragraph_xsmall" color="neutral_600">
                      ID: {farm.id}
                    </Text>
                  </Button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="farm-detail-content">
        {selectedUuid ? (
          selectedFarm ? (
            <>
              <FarmDetailCard
                farm={selectedFarm}
                loading={detailLoading}
                onEditClick={() => setEditOpen(true)}
              />
              {!detailLoading &&
                selectedFarm.inspections &&
                selectedFarm.inspections.length > 0 && (
                  <InspectionsTable inspections={selectedFarm.inspections} />
                )}
            </>
          ) : (
            <Stack align="center" className="farm-detail-empty">
              <Text variant="paragraph_medium" color="neutral_600">
                Loading farm details...
              </Text>
            </Stack>
          )
        ) : (
          <Stack align="center" className="farm-detail-empty">
            <Icon icon="MagnifyingGlass" size="large" color="neutral_400" />
            <Text variant="paragraph_medium" color="neutral_600">
              Search and select a farm above to view its details
            </Text>
          </Stack>
        )}
      </div>

      <EditFarmModal
        farm={selectedFarm ?? null}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
}
